/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import { groupBy, isEmpty, map, orderBy, sortBy } from 'lodash';
import { createSelector } from 'reselect';

import {
  getBalancesCurrentDate,
  getTransformAccountForHistoricalView
} from 'state/account/AccountOverviewTableSelectors';
import { productMap, productTypeNameFromNumber } from 'services/Products';
import {
  groupingFunctionalMapping,
  isProductSelection,
  mapAccountsToTableData
} from 'services/TableSupport';
import { filterFunctionMapping } from 'state/shared/FilterFunctionMappingUtil';
import { accountGrouping, ownerGrouping } from 'services/TableSupport';
import { getView, hasCollegeSavingsOnly } from 'state/view/ViewSelectors';
import {
  getAccountsWithIntraday,
  getMmrMessages
} from 'state/account/AccountSelectors';

const OWNERSHIP = 'ownership';

const getFilteringSelection = (state) => state.accountFilteringSelections;

const getPositionsFilteringSelection = createSelector(
  getFilteringSelection,
  hasCollegeSavingsOnly,
  (filteringSelections, hasCollegeSavingsOnly) => {
    const selection = {
      ...filteringSelections,
      grouping: hasCollegeSavingsOnly
        ? { selection: 'product' }
        : filteringSelections.grouping
    };

    return selection;
  }
);

export const getPositionsTableAccounts = createSelector(
  getPositionsFilteringSelection,
  getAccountsWithIntraday,
  (filteringSelections, accounts) => {
    return accounts
      .filter(
        (account) =>
          !account.purgedAccount &&
          filterFunctionMapping(
            filteringSelections.filtering.selection,
            productMap,
            filteringSelections.filtering.accountIds
          )(account)
      )
      .filter((a) => !a.hiddenAccount);
  }
);

export const getOrderedPositionsTableAccounts = createSelector(
  getPositionsFilteringSelection,
  getPositionsTableAccounts,
  (filteringSelections, accounts) =>
    orderBy(
      accounts,
      groupingFunctionalMapping(filteringSelections.grouping.selection, true)
    )
);

export const getPositionsTableAccountGroups = createSelector(
  getPositionsFilteringSelection,
  getOrderedPositionsTableAccounts,
  (filteringSelections, accounts) =>
    groupBy(
      accounts,
      groupingFunctionalMapping(filteringSelections.grouping.selection, false)
    )
);

export const getAccountGroupsForDownload = createSelector(
  getPositionsFilteringSelection,
  getBalancesCurrentDate,
  getView,
  getTransformAccountForHistoricalView,
  getPositionsTableAccountGroups,
  (filteringSelections, currentDate, view, transformAccount, accountGroups) =>
    map(accountGroups, (accountGroup) => {
      return mapAccountsToTableData({
        filteringSelections,
        currentDate,
        view,
        transformAccount
      })(accountGroup);
    })
);

export const getPositionsTableWithOwnership = createSelector(
  getPositionsTableAccountGroups,
  (accountGroups) =>
    map(accountGroups, (accountGroup) => {
      return groupBy(accountGroup, (account) => [
        accountGrouping(account),
        ownerGrouping(account)
      ]);
    })
);

const getRequestStatus = (state) => state.requestStatus;

export const getIsHistoricalBalancesLoading = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.historicalBalances === 'REQUESTED'
);

export const getPositionsTableMappedGroups = createSelector(
  getPositionsFilteringSelection,
  getBalancesCurrentDate,
  getView,
  getTransformAccountForHistoricalView,
  getPositionsTableWithOwnership,
  (filteringSelections, currentDate, view, transformAccount, accountGroups) =>
    map(accountGroups, (accountGroup) => {
      // Logic below is for when accountGroup is an object (filter group selection for product or ownership type)
      if (!Array.isArray(accountGroup)) {
        const { selection } = filteringSelections.grouping;
        const sortedAccounts = sortBy(
          Object.values(accountGroup),
          (accounts) => accounts[0].accountType
        );
        const aggregateGroup = sortedAccounts.reduce((acc, accounts) => {
          let key = OWNERSHIP;
          const children = mapAccountsToTableData({
            filteringSelections,
            currentDate,
            view,
            transformAccount,
            isProductOrOwnership: true
          })(accounts);

          if (isProductSelection(selection)) {
            const { productType } = accounts[0];
            key = productTypeNameFromNumber(productType);
          }

          if (acc[key]) {
            acc[key].children = acc[key].children.concat(children);
            acc[key].accounts = acc[key].accounts.concat(accounts);
            return acc;
          }

          acc[key] = {
            children: [].concat(children),
            accounts: [].concat(accounts)
          };
          return acc;
        }, {});

        // Create total group header object, but replace children with the children created created previously
        const { accounts, children } = Object.values(aggregateGroup)[0];
        const positionsTableData = mapAccountsToTableData({
          filteringSelections,
          currentDate,
          view,
          transformAccount,
          isProductOrOwnership: false
        })(accounts);
        positionsTableData.children = children;
        return positionsTableData;
      }
      // Standard construction of the positionsTableData because accountGroup is an array of object.
      return mapAccountsToTableData({
        filteringSelections,
        currentDate,
        view,
        transformAccount
      })(accountGroup);
    })
);

export const getUpdatedPositionsTableDataWithMmr = createSelector(
  getPositionsTableMappedGroups,
  getMmrMessages,
  (tableGroups, mmrMessages) => {
    if (!isEmpty(mmrMessages)) {
      const { messages } = mmrMessages;
      return tableGroups.map((group) => {
        const updatedGroup = group.children.map((subGroup) => {
          const updatedChildren = subGroup.children.map((child) => {
            const {
              details: {
                name: { ticker }
              }
            } = child;
            return {
              ...child,
              mmrMessage: messages ? messages.get(ticker) : ''
            };
          });
          return {
            ...subGroup,
            children: updatedChildren
          };
        });
        return {
          ...group,
          children: updatedGroup
        };
      });
    }
    return tableGroups;
  }
);
